import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOMeta from "@/components/feature/SEOMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const trustItems = [
  {
    icon: "ri-shield-check-line",
    label: "内容审核",
    desc: "严格的内容审核机制，确保资源质量",
  },
  {
    icon: "ri-refresh-line",
    label: "每日更新",
    desc: "每日200+精品内容持续更新",
  },
  {
    icon: "ri-vip-crown-line",
    label: "大师汇聚",
    desc: "500+顶级探花大师独家入驻",
  },
  {
    icon: "ri-user-smile-line",
    label: "用户至上",
    desc: "以用户体验为核心的服务理念",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "专业支持",
    desc: "7x24小时专业技术支持团队",
  },
];

const faqs = [
  {
    question: "探花系列平台是什么？",
    answer:
      "探花系列精品聚合平台是一个专注于汇聚国内探花、探花精选、探花91等优质资源的内容聚合平台。我们致力于为用户提供全面、及时、高质量的探花内容浏览体验。",
  },
  {
    question: "平台内容更新频率如何？",
    answer:
      "平台每日更新200+精品资源，涵盖国内探花、探花精选、探花91、探花直播、顶级探花大师等多个分类，确保用户能够第一时间获取最新内容。",
  },
  {
    question: "如何联系探花系列平台？",
    answer:
      "您可以通过页面底部的邮箱或社交媒体账号联系我们。我们的客服团队将在24小时内回复您的咨询。",
  },
  {
    question: "平台上的内容是否经过审核？",
    answer:
      "是的，所有内容均经过专业团队的严格审核，确保内容质量与合规性。我们建立了完善的内容审核机制，为用户提供安全可靠的浏览环境。",
  },
  {
    question: "探花系列平台支持哪些设备？",
    answer:
      "我们的平台全面支持PC端和移动端访问，网站采用响应式设计，无论您使用手机、平板还是电脑，都能获得最佳的浏览体验。",
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [{ label: "关于我们" }];

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "探花系列精品聚合平台",
    url: "http://lalahzz.cn/",
    logo: "http://lalahzz.cn/logo.png",
    description:
      "汇聚国内探花、探花精选、探花91等优质资源，打造探花爱好者必看的精品聚合平台。",
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "客服",
      availableLanguage: "Chinese",
    },
  };

  const pageJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 探花系列精品聚合平台",
    description:
      "了解探花系列精品聚合平台的品牌故事、发展理念与服务优势。",
    url: "http://lalahzz.cn/about",
    isPartOf: {
      "@type": "WebSite",
      name: "探花系列精品聚合平台",
      url: "http://lalahzz.cn/",
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://lalahzz.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "关于我们",
        item: "http://lalahzz.cn/about",
      },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 探花系列精品聚合平台"
        description="了解探花系列精品聚合平台的品牌故事、发展理念与服务优势。汇聚国内探花、探花精选、探花91等优质资源，打造探花爱好者必看的精品聚合平台。"
        keywords="探花系列,关于我们,品牌介绍,探花平台,联系我们"
        canonical="http://lalahzz.cn/about"
        ogType="website"
        jsonLd={[orgJsonLd, pageJsonLd, breadcrumbJsonLd, faqJsonLd]}
      />
      <Navbar />

      <main>
        {/* Brand Introduction */}
        <section className="bg-background-900 text-background-100 pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-8 text-center">
              <span className="text-sm text-background-400 tracking-widest uppercase">
                About Us
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mt-4 leading-tight">
                探花系列
                <br />
                精品聚合平台
              </h1>
              <p className="mt-6 text-base md:text-lg text-background-400 max-w-2xl mx-auto leading-relaxed">
                我们致力于打造探花爱好者必看的精品聚合基地，汇聚国内探花、探花精选、探花91等优质资源，为用户提供全面、及时、高质量的内容浏览体验。
              </p>
              <div className="mt-8 flex items-center justify-center gap-4">
                <Link
                  to="/category/selection"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-950 rounded-md font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  浏览内容
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-background-400 text-background-100 rounded-md font-medium text-sm hover:bg-background-800 transition-colors whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Reputation */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                平台优势
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                五大核心优势，为您提供卓越的内容聚合体验
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {trustItems.map((item) => (
                <div
                  key={item.label}
                  className="bg-background-100 rounded-xl p-5 md:p-6 text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-background-200/70 flex items-center justify-center mx-auto">
                    <i className={`${item.icon} w-6 h-6 flex items-center justify-center text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-950 mt-3">
                    {item.label}
                  </h3>
                  <p className="text-xs text-foreground-600 mt-1.5 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: "10,000+", label: "精品资源" },
                { value: "500,000+", label: "活跃用户" },
                { value: "200+", label: "日更内容" },
                { value: "500+", label: "入驻大师" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-600">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600 mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
                  <i className="ri-mail-line w-6 h-6 flex items-center justify-center text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mt-3">
                  电子邮箱
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">
                  hello@lalahzz.cn
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
                  <i className="ri-customer-service-line w-6 h-6 flex items-center justify-center text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mt-3">
                  客服热线
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">
                  400-888-9999
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
                  <i className="ri-time-line w-6 h-6 flex items-center justify-center text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mt-3">
                  服务时间
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">
                  全年无休 7x24小时
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                关于探花系列平台的常见问题解答
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-950">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-24">
          <div className="absolute inset-0 bg-background-900"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-10 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
              立即探索探花系列精品内容
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-400 max-w-xl mx-auto">
              加入数十万探花爱好者的行列，第一时间获取最新精品资源与行业动态
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Link
                to="/category/selection"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                开始浏览
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-background-600 text-background-200 rounded-md font-medium text-sm hover:bg-background-800 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}